import React, {useEffect, useState} from 'react';
import {
  Box,
  FormControl,
  FormHelperText,
  TextField,
  Typography,
  Autocomplete,
  Button,
  Tooltip,
  IconButton,
  FormLabel,
} from '@mui/material';
import {Info} from '@mui/icons-material';
import {gql, useMutation, useQuery} from '@apollo/client';
import {useAccountContext} from '../../contexts/AccountContext';
import OWNERSHIP from '../../constants/Ownership';
import {recommendFieldsType} from '../../features/recommendations/CreateRecommend/recommendFields';
import {ShowIfDebugWrapper} from '../../contexts/DebugProvider';
import {CREATE_GOAL, UPDATE_GOAL} from '../../features/goals/goalsQueries';
import {CreateGoal, CreateGoalVariables} from '../../schema-types/CreateGoal';
import {UpdateGoal, UpdateGoalVariables} from '../../schema-types/UpdateGoal';
import {useReviewContext} from '../../contexts/ReviewContext';
import {useDjangoContext} from '../../contexts/useAuthContext';
import {
  BusinessUnitEnum,
  GlobalPermissionEnum,
  OVRecommendActionEnum,
} from '../../schema-types/globalTypes';
import {useAutoForm, useAutoField} from '../AutoForm/useAutoForm';
import {
  ListGoalBasic,
  ListGoalBasicVariables,
  ListGoalBasic_goals,
} from '../../schema-types/ListGoalBasic';
import {FeatureBoundary} from '../../contexts/FeatureBoundary';
import {themeColors} from '../../theme/Theme';
import {SaltusAny} from '../../utils/SaltusAny';
import validateUUID from '../../utils/validateUUID';
import {getFilteredGoalFormValues} from '../../features/goals/Goals/utils';
import {UPDATES} from '../../shared/UPDATES';

type Props = {
  value: ListGoalBasic_goals | null | undefined;
  onChange: (goal: ListGoalBasic_goals) => void;
  validationError: boolean;
};
// TODO: make this a dumb component and remove query from it.
function GoalsDropdown({value, onChange, validationError}: Props) {
  const {values, errors} = useAutoForm<recommendFieldsType>();
  const {contact} = values;
  const {accountType, accountId} = useAccountContext();
  const {reviewId} = useReviewContext();
  const [addedDummyGoal, setAddedDummyGoal] = useState(false);
  const {
    loading: goalsLoading,
    data: goalsData,
    error: goalsError,
    refetch: refetchGoals,
  } = useQuery<ListGoalBasic, ListGoalBasicVariables>(
    gql`
      query ListGoalBasic(
        $accountId: UUID!
        $reviewId: UUID
        $hideNoLongerRequired: Boolean
      ) {
        goals(
          accountId: $accountId
          reviewId: $reviewId
          hideNoLongerRequired: $hideNoLongerRequired
        ) {
          id
          name
          contact {
            id
          }
          ownership
        }
      }
    `,
    {
      variables: {
        accountId: validateUUID(accountId!)!,
        reviewId: validateUUID(reviewId!),
        hideNoLongerRequired: true,
      },
      nextFetchPolicy: 'network-only',
      fetchPolicy: 'network-only',
    }
  );
  const [createGoal, {loading: createGoalLoading}] = useMutation<
    CreateGoal,
    CreateGoalVariables
  >(CREATE_GOAL, {
    variables: {
      accountId: validateUUID(accountId!)!,
    },
  });
  const [updateGoal, {loading: updateGoalLoading}] = useMutation<
    UpdateGoal,
    UpdateGoalVariables
  >(UPDATE_GOAL, {});
  const [goals, setGoals] = useState<ListGoalBasic_goals[]>([]);
  // The select component satisfies the need for highly optimised rendering
  const onInputChange = (event: SaltusAny, val?: SaltusAny) => {
    const newGoal = goals.find((g) => g.id === val?.id || event?.target?.value);
    if (newGoal) {
      onChange(newGoal);
    }
  };

  useEffect(() => {
    if (!goalsData || (goalsData?.goals?.length || 0) < 1) {
      return;
    }
    // Goals, if one owner selected -> Account, joint and personal goals.
    // if joint selected - joint and account goals
    if (contact === OWNERSHIP.JOINT_CODE) {
      setGoals(
        (goalsData?.goals || []).filter(
          (goal) => goal?.ownership !== OWNERSHIP.SINGLE_LIVES
        )
      );
    } else {
      // Only personal goals relevant to the selected contact should be chosen
      setGoals(
        (goalsData?.goals || []).filter(
          (goal) =>
            goal.ownership !== OWNERSHIP.SINGLE_LIVES ||
            (goal.ownership === OWNERSHIP.SINGLE_LIVES && goal?.contact?.id === contact)
        )
      );
    }
  }, [goalsData, contact]);

  useEffect(() => {
    // if DA, auto select first goal
    if (accountType === BusinessUnitEnum.DA) {
      if (goals && goals.length) {
        onChange(goals[0]);
      }
    }
  }, [goals, accountType]);

  useEffect(() => {
    if (accountType === BusinessUnitEnum.DA) {
      return;
    }
    if (addedDummyGoal) {
      const dummyGoal = goals.find((goal) => goal.name?.includes('dummy goal'));
      if (dummyGoal) {
        onChange(dummyGoal);
      }
    }
  }, [accountType, addedDummyGoal]);

  return (
    <FormControl
      sx={{
        minWidth: 300,
        '& .MuiInputBase-root': {
          minWidth: '10rem',
        },
      }}
      required={values.planAction !== OVRecommendActionEnum.ACTION}
      error={Boolean(goalsError)}
      size="small"
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <FormLabel>
          <strong>Goal:</strong>
        </FormLabel>
        <Tooltip
          title={
            <>
              <Typography mb={2} color={themeColors.white}>
                The selected goal will be linked with the new requirement
              </Typography>
              <Typography color={themeColors.white}>
                If you cannot find a goal, check it is inline with the client level of
                your recommendation (Joint or correct contact).
              </Typography>
            </>
          }
        >
          <IconButton size="small">
            <Info />
          </IconButton>
        </Tooltip>
      </Box>
      <Autocomplete
        id="goalSelect"
        data-test="Select Goal"
        value={value || ''}
        size="small"
        isOptionEqualToValue={(option, val) => option.id === val.id}
        onChange={onInputChange}
        disabled={goalsLoading || !!goalsError}
        defaultValue={value?.id}
        options={goals || []}
        getOptionLabel={(s) => s?.name || ''}
        renderInput={(params) => (
          <TextField
            error={!!errors?.goal}
            variant="outlined"
            {...params}
            size="small"
            label={goalsLoading ? 'Select a goal loading' : 'Select a goal:'}
          />
        )}
      />
      <ShowIfDebugWrapper debugKey="forms">
        <Button
          disabled={updateGoalLoading || createGoalLoading || goalsLoading}
          onClick={() => {
            if (goals?.filter((goal) => goal.name?.includes('dummy goal')).length) {
              setAddedDummyGoal(true);
              return;
            }
            return createGoal()
              .then((res) => {
                if (res.data?.createGoal?.goal?.id) {
                  return updateGoal({
                    variables: {
                      goalId: res.data?.createGoal?.goal?.id,
                      reviewId: validateUUID(reviewId!)!,
                      name: 'dummy goal',
                      ...getFilteredGoalFormValues(),
                    },
                  });
                }
              })
              .then(() => refetchGoals())
              .then(() => setAddedDummyGoal(true));
          }}
        >
          {addedDummyGoal ? 'Dummy goal added' : 'Debug: Add dummy goal'}
        </Button>
      </ShowIfDebugWrapper>
      <Box alignItems="center" display="flex">
        <FormHelperText sx={{flex: 1}}>
          {goalsError ? 'An error occurred while trying to get the list of goals' : null}
        </FormHelperText>
      </Box>

      {/* Used for testing */}
      <select
        data-test-select="Select Goal"
        value={value?.id || ''}
        onChange={onInputChange}
        onSelect={onInputChange}
        style={{display: 'none'}}
      >
        {goals?.map((goal) => (
          <option value={goal.id} key={goal.id}>
            {goal.id}
          </option>
        ))}
      </select>
    </FormControl>
  );
}

export function GoalsSelect() {
  const [goalField, goalMeta, goalHelpers] =
    useAutoField<recommendFieldsType['goal']>('goal');
  const {perms} = useDjangoContext();

  if (!perms.includes(GlobalPermissionEnum.can_access_ehons_notes_goals)) {
    return <Box display="flex" alignItems="center" />;
  }

  return (
    <>
      <Box display="flex" alignItems="center">
        <GoalsDropdown
          validationError={!!goalMeta.error}
          onChange={(selectedGoal) => {
            goalHelpers.setValue(UPDATES._012, selectedGoal);
          }}
          value={goalField.value}
        />
        <FeatureBoundary
          features={[GlobalPermissionEnum.can_access_ehons_notes_goals]}
          showErrorIfDenied={false}
        />
      </Box>
    </>
  );
}

export default GoalsSelect;
